/**
 * Sentry Client Configuration
 *
 * This file configures the initialization of Sentry on the client.
 * The config you add here will be used whenever a users loads a page in their browser.
 * https://docs.sentry.io/platforms/javascript/guides/nextjs/
 */
import posthog from "posthog-js";

// Sentry is gated to production/preview so the SDK (replay, browser tracing,
// integrations) isn't bundled into client chunks during local dev.
const sentryEnabled = process.env.NODE_ENV === "production" || process.env.VERCEL_ENV === "preview";

if (sentryEnabled) {
  void import("@sentry/nextjs").then((Sentry) => {
    Sentry.init({
      dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

      tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,

      debug: false,
      enabled: true,

      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 0,

      integrations: [
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
        Sentry.browserTracingIntegration(),
      ],

      // Filter out sensitive data for HIPAA compliance
      beforeSend(event) {
        if (event.request?.data) {
          const sensitiveFields = [
            "ssn",
            "social_security",
            "dob",
            "date_of_birth",
            "mrn",
            "medical_record",
            "insurance",
            "diagnosis",
            "medication",
            "password",
            "token",
            "secret",
          ];

          const data = typeof event.request.data === "string" ? event.request.data : JSON.stringify(event.request.data);

          for (const field of sensitiveFields) {
            if (data.toLowerCase().includes(field)) {
              event.request.data = "[REDACTED - Contains sensitive data]";
              break;
            }
          }
        }

        return event;
      },

      environment: process.env.VERCEL_ENV || process.env.NODE_ENV || "development",
    });
  });
}

posthog.init(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN!, {
  api_host: "/ingest",
  ui_host: "https://us.posthog.com",
  defaults: "2026-01-30",
  capture_exceptions: true,
  debug: process.env.NODE_ENV === "development",
});
